import React, { type InputHTMLAttributes, type ReactNode, useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import toInteger from 'lodash/toInteger';
import toString from 'lodash/toString';

import './DepInput.scss';

const REMAINING_CHARACTERS_WARN_THRESHOLD = 25;

export enum DepInputVariant {
    Primary = 'primary',
    Light = 'light',
}

export enum DepInputSize {
    Compact = 'compact',
    Normal = 'normal',
}

export interface DepInputProps extends InputHTMLAttributes<HTMLInputElement> {
    /** Class string to append to the Input's class string */
    classes?: string;
    /** Text label for the input */
    label?: string;
    /** Is the input value invalid? */
    isInvalid?: boolean;
    /** The error to display below an invalid input */
    errorLabel?: string;
    /** They style variant of the input */
    variant?: DepInputVariant;
    /** The size of the input */
    sizeVariant?: DepInputSize;
    /** JSX to append to the input, within the left side of the input's boundaries */
    append?: ReactNode;
    /** JSX to prepend to the input, within the right side of the input's boundaries */
    prepend?: ReactNode;
    /** To facilitate border radius animations */
    borderRadius?: string;
}

export function DepInput({
    isInvalid = false,
    errorLabel,
    variant,
    sizeVariant = DepInputSize.Normal,
    classes,
    label,
    prepend,
    append,
    onFocus,
    onBlur,
    // TODO: We should not default to `autoFocus`,
    // as re-renders can lead to "competition" between
    // elements that should retain focus.
    autoFocus = true,
    ...props
}: DepInputProps) {
    const [isFocused, setIsFocused] = useState(false);
    const [invalid, setInvalid] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // for inputs with limited length
    const maxLength = toInteger(props.maxLength) || Infinity;
    const remainingCharacters = Math.max(maxLength - (toString(props.value).length || 0), 0);

    useEffect(() => {
        setInvalid(isInvalid);
    }, [isInvalid]);

    const focusedNotEmpty = !!props.value && !isFocused;
    const containerClasses = cx(
        'yat-standard-input',
        {
            'yat-standard-input--focused': focusedNotEmpty || isFocused,
            [`yat-standard-input--${variant}`]: variant,
            [`yat-standard-input--${sizeVariant}`]: sizeVariant,
            'yat-standard-input--invalid': invalid,
        },
        classes,
    );

    function handleFocus(event) {
        setIsFocused(true);
        if (onFocus) onFocus(event);
    }

    function handleBlur(event) {
        setIsFocused(false);
        if (onBlur) onBlur(event);
    }

    return (
        <div className={containerClasses}>
            <div
                role="button"
                className="yat-standard-input__fake-input"
                // onClick={() => inputRef.current?.focus()}
                // onFocus={() => inputRef.current?.focus()}
            >
                {prepend}
                <div className="yat-standard-input__input-and-label">
                    <label className="yat-standard-input__label">{label}</label>
                    <input
                        {...props}
                        className="yat-standard-input__input"
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        autoFocus={autoFocus ? true : undefined}
                        tabIndex={0}
                        ref={inputRef}
                    />
                </div>
                {remainingCharacters <= REMAINING_CHARACTERS_WARN_THRESHOLD && (
                    <var className="yat-standard-input__remaining">{remainingCharacters}</var>
                )}
                {append}
            </div>
            {invalid && errorLabel && <div className="yat-standard-input__error-label">{errorLabel}</div>}
        </div>
    );
}
